// assets/AllCourses/Exercises/CrosswordExercise.jsx
"use client";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

const BLACK_CELL = "#";

export default function CrosswordExercise({
  exercise = {},
  onUpdate, // Only in edit mode
  errors = {},
  isPreview = false, // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student: report correct/incorrect
}) {
  const isEditMode = typeof onUpdate === "function";

  // Normalize crossword data
  const crosswordData = exercise.crosswordData || { grid: [], clues: { across: [], down: [] } };
  const grid = Array.isArray(crosswordData.grid)
    ? crosswordData.grid.map(row => Array.isArray(row) ? row.map(c => typeof c === "string" ? c : "") : [])
    : [];

  const gridSize = grid.length > 0 ? grid.length : 0;

  const clues = {
    across: Array.isArray(crosswordData.clues?.across)
      ? crosswordData.clues.across.map(c => ({
          number: Number(c.number) || 0,
          row: Number(c.row) || 0,
          col: Number(c.col) || 0,
          clue: typeof c.clue === "string" ? c.clue : "",
          answer: typeof c.answer === "string" ? c.answer.toUpperCase().replace(/\s+/g, "") : "",
          requireCapitalization: Boolean(c.requireCapitalization),
        }))
      : [],
    down: Array.isArray(crosswordData.clues?.down)
      ? crosswordData.clues.down.map(c => ({
          number: Number(c.number) || 0,
          row: Number(c.row) || 0,
          col: Number(c.col) || 0,
          clue: typeof c.clue === "string" ? c.clue : "",
          answer: typeof c.answer === "string" ? c.answer.toUpperCase().replace(/\s+/g, "") : "",
          requireCapitalization: Boolean(c.requireCapitalization),
        }))
      : [],
  };

  // Student state
  const emptyUserGrid = gridSize > 0
    ? Array.from({ length: gridSize }, () => Array.from({ length: gridSize }, () => ""))
    : [];

  const [userGrid, setUserGrid] = useState(emptyUserGrid);
  const [hasInteracted, setHasInteracted] = useState(false);
  const completedRef = useRef(false);

  // Sync saved attempt (student mode)
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;
    setUserGrid(Array.isArray(exercise.attempt.userGrid) ? exercise.attempt.userGrid : emptyUserGrid);
    setHasInteracted(!!exercise.attempt.hasInteracted);
  }, [isPreview, exercise.attempt]);

  // Reset on exercise change (student mode)
  useEffect(() => {
    if (!isPreview) return;
    completedRef.current = false;
    setUserGrid(emptyUserGrid);
    setHasInteracted(false);
  }, [exercise._id, isPreview]);

  // === EDIT MODE FUNCTIONS ===
  const updateCrossword = (nextData) => {
    if (isEditMode) onUpdate("crosswordData", nextData);
  };

  const createGrid = (size = 10) => {
    if (!isEditMode) return;
    const emptyRow = Array(size).fill("");
    updateCrossword({
      grid: Array(size).fill(emptyRow.map(() => "")),
      clues: { across: [], down: [] },
    });
  };

  const handleCellChange = (rIdx, cIdx, value) => {
    if (!isEditMode) return;
    let letter = "";
    if (value === "#") {
      letter = BLACK_CELL;
    } else if (value.length > 0) {
      letter = value.slice(0, 1).toUpperCase();
    }
    const nextGrid = grid.map((row, r) =>
      row.map((cell, c) => (r === rIdx && c === cIdx ? letter : cell))
    );
    updateCrossword({ ...crosswordData, grid: nextGrid });
  };

  const handleClueChange = (type, idx, field, value) => {
    if (!isEditMode) return;
    const nextClues = [...clues[type]];
    const current = nextClues[idx] || { number: 0, row: 0, col: 0, clue: "", answer: "", requireCapitalization: false };
    nextClues[idx] = {
      ...current,
      [field]:
        field === "answer"
          ? String(value).toUpperCase().replace(/\s+/g, "")
          : ["number", "row", "col"].includes(field)
          ? Number(value) || 0
          : value,
    };
    updateCrossword({
      ...crosswordData,
      clues: { ...clues, [type]: nextClues },
    });
  };

  const handleCapitalizationChange = (type, idx, checked) => {
    if (!isEditMode) return;
    const nextClues = [...clues[type]];
    nextClues[idx] = {
      ...nextClues[idx],
      requireCapitalization: Boolean(checked),
    };
    updateCrossword({
      ...crosswordData,
      clues: { ...clues, [type]: nextClues },
    });
  };

  const handleAddClue = (type) => {
    if (!isEditMode) return;
    updateCrossword({
      ...crosswordData,
      clues: {
        ...clues,
        [type]: [...clues[type], { number: 0, row: 0, col: 0, clue: "", answer: "", requireCapitalization: false }],
      },
    });
  };

  const handleRemoveClue = (type, idx) => {
    if (!isEditMode) return;
    updateCrossword({
      ...crosswordData,
      clues: {
        ...clues,
        [type]: clues[type].filter((_, i) => i !== idx),
      },
    });
  };

  // === STUDENT MODE FUNCTIONS ===
  const getClueNumber = (r, c) => {
    return (
      clues.across.find(cl => cl.row === r && cl.col === c)?.number ||
      clues.down.find(cl => cl.row === r && cl.col === c)?.number ||
      null
    );
  };

  const isBlackCell = (r, c) => grid[r]?.[c] === BLACK_CELL;

  const isActiveCell = (r, c) => !isBlackCell(r, c) && grid[r]?.[c] !== "";

  const handleStudentChange = (r, c, value) => {
    if (isEditMode || showResult || isBlackCell(r, c) || !isActiveCell(r, c)) return;
    if (!hasInteracted) setHasInteracted(true);
    const letter = value.slice(-1).toUpperCase();
    setUserGrid(prev => {
      const next = prev.map((row, ri) =>
        row.map((cell, ci) => (ri === r && ci === c ? letter : cell))
      );
      // Auto-check when all active cells filled
      const allFilled = grid.every((row, ri) =>
        row.every((cell, ci) => isBlackCell(ri, ci) || cell === "" || next[ri][ci] !== "")
      );
      if (allFilled) {
        checkAnswers(next);
      }
      return next;
    });
  };

  const checkAnswers = (currentGrid) => {
    if (completedRef.current || isEditMode) return;
    completedRef.current = true;
    let allCorrect = true;
    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (!isActiveCell(r, c)) continue;
        const entered = String(currentGrid[r][c] || "").trim();
        const expected = String(grid[r][c]).trim();
        const clue = clues.across.find(cl => cl.row === r && cl.col === c) ||
                     clues.down.find(cl => cl.row === r && cl.col === c);
        let correct = entered.toUpperCase() === expected.toUpperCase();
        if (clue?.requireCapitalization) {
          correct = entered === expected;
        }
        if (!correct) allCorrect = false;
      }
    }
    onComplete(exercise._id, allCorrect);
  };

  // === RENDER ===
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {gridSize === 0 && (
          <div className="text-center py-8">
            <Button onClick={() => createGrid(10)}>
              Create 10×10 Crossword Grid
            </Button>
            <p className="mt-2 text-sm text-gray-600">Use # for black cells</p>
          </div>
        )}
        {gridSize > 0 && (
          <>
            <div className="flex justify-center">
              <div
                className="grid gap-1 bg-gray-200 p-4 rounded-lg"
                style={{ gridTemplateColumns: `repeat(${gridSize}, 48px)` }}
              >
                {grid.map((row, rIdx) =>
                  row.map((cell, cIdx) => (
                    <Input
                      key={`${rIdx}-${cIdx}`}
                      maxLength={1}
                      value={cell === BLACK_CELL ? "#" : cell}
                      onChange={(e) => handleCellChange(rIdx, cIdx, e.target.value)}
                      className={cn(
                        "w-12 h-12 text-center text-lg font-bold uppercase",
                        cell === BLACK_CELL && "bg-black text-white"
                      )}
                      placeholder="."
                    />
                  ))
                )}
              </div>
            </div>
            {["across", "down"].map((type) => (
              <div key={type} className="space-y-4">
                <h3 className="font-bold text-lg capitalize">{type} Clues</h3>
                {clues[type].map((c, idx) => (
                  <Card key={idx} className="p-4 space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <Input type="number" label="Number" value={c.number} onChange={(e) => handleClueChange(type, idx, "number", e.target.value)} min={1} />
                      <Input type="number" label="Start Row" value={c.row} onChange={(e) => handleClueChange(type, idx, "row", e.target.value)} min={0} max={gridSize - 1} />
                      <Input type="number" label="Start Col" value={c.col} onChange={(e) => handleClueChange(type, idx, "col", e.target.value)} min={0} max={gridSize - 1} />
                    </div>
                    <Input label="Clue Text" value={c.clue} onChange={(e) => handleClueChange(type, idx, "clue", e.target.value)} placeholder="Capital of France?" />
                    <Input label="Answer (no spaces)" value={c.answer} onChange={(e) => handleClueChange(type, idx, "answer", e.target.value)} placeholder="PARIS" className="uppercase" />
                    <CapitalizationRule checked={!!c.requireCapitalization} onChange={(checked) => handleCapitalizationChange(type, idx, checked)} />
                    <Button variant="destructive" size="sm" onClick={() => handleRemoveClue(type, idx)}>
                      Remove Clue
                    </Button>
                  </Card>
                ))}
                <Button variant="outline" onClick={() => handleAddClue(type)}>
                  Add {type.charAt(0).toUpperCase() + type.slice(1)} Clue
                </Button>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  // Student mode
  if (gridSize === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>No valid crossword grid available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardDescription>Fill in the crossword grid using the clues below.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8 md:grid md:grid-cols-2 md:gap-10 md:space-y-0">
        <div className="flex justify-center">
          <div
            className="grid gap-0.5 bg-gray-300 p-4 rounded-xl"
            style={{ gridTemplateColumns: `repeat(${gridSize}, 50px)` }}
          >
            {userGrid.map((row, rIdx) =>
              row.map((cell, cIdx) => {
                const black = isBlackCell(rIdx, cIdx);
                const active = isActiveCell(rIdx, cIdx);
                const clueNumber = getClueNumber(rIdx, cIdx);
                const correct = showResult && active
                  ? cell.toUpperCase() === grid[rIdx][cIdx].toUpperCase()
                  : true;
                return (
                  <div key={`${rIdx}-${cIdx}`} className="relative">
                    {clueNumber && (
                      <span className="absolute -top-2 -left-1 text-xs font-bold text-gray-700 z-10">
                        {clueNumber}
                      </span>
                    )}
                    {black ? (
                      <div className="w-12 h-12 bg-black rounded-sm" />
                    ) : (
                      <Input
                        maxLength={1}
                        value={cell}
                        disabled={!active || showResult}
                        onChange={(e) => handleStudentChange(rIdx, cIdx, e.target.value)}
                        className={cn(
                          "w-12 h-12 text-center text-xl font-bold uppercase rounded-sm",
                          !active && "bg-gray-200 cursor-not-allowed",
                          showResult && active && correct && "bg-green-100 border-green-600",
                          showResult && active && !correct && "bg-red-100 border-red-600"
                        )}
                      />
                    )}
                    {showResult && active && !correct && (
                      <span className="absolute bottom-0 right-0 text-xs text-red-700 font-bold">
                        {grid[rIdx][cIdx]}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="space-y-6">
          <div>
            <h3 className="font-bold text-lg mb-3">Across</h3>
            <ul className="space-y-1 text-sm">
              {clues.across.sort((a, b) => a.number - b.number).map((c) => (
                <li key={c.number} className="text-gray-700">
                  <span className="font-semibold">{c.number}.</span> {c.clue}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-3">Down</h3>
            <ul className="space-y-1 text-sm">
              {clues.down.sort((a, b) => a.number - b.number).map((c) => (
                <li key={c.number} className="text-gray-700">
                  <span className="font-semibold">{c.number}.</span> {c.clue}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}